'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Eraser,
  Eye,
  Loader2,
  Search,
  Sparkles,
  X
} from 'lucide-react';
import { Icon } from '@iconify/react';
import '@/lib/iconify-solar';

import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import { isSuperAdmin, hasAnyPermission } from '@/features/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { permissionQueries } from '../services/roles.queries';
import { useUpdateRoleMutation } from '../services/roles.mutations';
import { groupPermissions, getRoleDisplayName } from '../mappers/role.mapper';
import { RoleColorBadge } from './role-color-badge';
import {
  GROUP_ICON_MAP,
  GROUP_KEY_MAP,
  PERMISSION_CATEGORIES,
  CHILD_GROUP_MAP,
  getGroupCategory,
  getPermissionAction,
  isColumnPermission,
  isHiddenPermission,
  translateAction
} from './role-permissions-matrix-config';
import { RoleViewPermissionsPanel } from './role-view-permissions-panel';
import { useHiddenPermissionGroups } from './use-hidden-permission-groups';
import type { Role, Permission } from '../types';

/**
 * Permissions whose UI controls are removed from the panel: per-column
 * toggles (`{group}.column.{field}` / `{group}.dynamic_column.{field}`),
 * the View Columns button gate (`{group}.view_filter`), and any permission
 * belonging to a hidden group (disabled features). Roles that already
 * carry these perms keep them — they're hidden, not stripped on save.
 */
function isViewColumnsPermission(
  name: string,
  hiddenGroups: ReadonlySet<string>
): boolean {
  return (
    isColumnPermission(name) ||
    name.endsWith('.view_filter') ||
    isHiddenPermission(name, hiddenGroups)
  );
}

interface RolePermissionsPanelProps {
  role: Role | null;
  locale: string;
  onClose: () => void;
}

export function RolePermissionsPanel({
  role,
  locale,
  onClose
}: RolePermissionsPanelProps) {
  const t = useTranslations('roles');
  const tRoot = useTranslations();
  const { user: authUser } = useAuth();
  // The Super Admin role is intentionally locked from the UI — its permission
  // set is the seeder's source of truth (every permission, always). Allowing
  // edits here would silently desync the role until the next seed.
  const isReadOnlyRole = role?.name === 'Super Admin';
  const canEdit =
    !isReadOnlyRole &&
    (isSuperAdmin(authUser) ||
      hasAnyPermission(authUser, ['roles.create', 'roles.edit']));
  const [localPermissions, setLocalPermissions] = useState<string[]>([]);
  const [tab, setTab] = useState<'actions' | 'views'>('actions');
  const [search, setSearch] = useState('');
  const [groupOpenState, setGroupOpenState] = useState<Record<string, boolean>>(
    {}
  );
  const [categoryOpenState, setCategoryOpenState] = useState<
    Record<string, boolean>
  >({});
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  // Cache the most recent non-null role so the sheet keeps rendering its
  // content during the close animation (when `role` flips back to null).
  const [activeRole, setActiveRole] = useState<Role | null>(role);
  if (role && role !== activeRole) {
    setActiveRole(role);
  }
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Autosave is serialized: one PUT at a time, with only the LATEST pending set
  // kept while a save is in flight. Firing them concurrently made two requests
  // race inside the backend's permission sync, and the loser came back as a raw
  // `Duplicate entry '117-5' for key 'role_has_permissions.PRIMARY'` next to the
  // winner's success toast. The backend now locks the role row too, so this is
  // the belt to that braces — but it also means one less redundant round-trip
  // per checkbox burst.
  const inFlightRef = useRef(false);
  const queuedPermissionsRef = useRef<string[] | null>(null);
  const savedIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const prevRoleIdRef = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isOpen = !!role;
  const hasSearch = search.trim().length > 0;

  const { data: permissionsData } = useQuery(permissionQueries.list());
  const updateMutation = useUpdateRoleMutation();
  const hiddenGroups = useHiddenPermissionGroups();

  // Sync local state when role changes
  const currentRoleId = role?.id ?? null;
  if (currentRoleId !== prevRoleIdRef.current) {
    prevRoleIdRef.current = currentRoleId;
    if (role) {
      setLocalPermissions(role.permissions);
      // Reset section open state on role switch so each role starts fresh.
      setGroupOpenState({});
      setCategoryOpenState({});
      setSearch('');
      setTab('actions');
    }
  }

  const allPermissions = useMemo(
    () => permissionsData?.data ?? [],
    [permissionsData]
  );

  // Permissions surfaced in the panel UI. View-columns perms (per-column
  // toggles + view_filter) are hidden but stay valid for save-side checks.
  const displayPermissions = useMemo(
    () =>
      allPermissions.filter(
        (p) => !isViewColumnsPermission(p.name, hiddenGroups)
      ),
    [allPermissions, hiddenGroups]
  );

  // Drop permission names that no longer exist server-side (e.g. orphans
  // removed by a seeder run while this panel was open). Without this, the
  // debounced save would include stale names and the backend would reject
  // the whole payload via the `exists:permissions,name` rule. Done during
  // render (not in an effect) so the next save sees the cleaned list
  // immediately, mirroring the same pattern used for `activeRole` above.
  const [lastSeenAllPermissions, setLastSeenAllPermissions] =
    useState(allPermissions);
  if (allPermissions.length > 0 && allPermissions !== lastSeenAllPermissions) {
    setLastSeenAllPermissions(allPermissions);
    const validNames = new Set(allPermissions.map((p) => p.name));
    const cleaned = localPermissions.filter((n) => validNames.has(n));
    if (cleaned.length !== localPermissions.length) {
      setLocalPermissions(cleaned);
    }
  }

  const permissionGroups = useMemo(
    () => groupPermissions(displayPermissions),
    [displayPermissions]
  );

  const totalPermissions = displayPermissions.length;

  const getActionLabel = useCallback(
    (permName: string) => translateAction(getPermissionAction(permName), t),
    [t]
  );

  const getGroupLabel = useCallback(
    (group: string) => {
      const groupKey = GROUP_KEY_MAP[group] ?? 'group_other';
      return t(groupKey);
    },
    [t]
  );

  // Filter groups/permissions by search
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return permissionGroups;

    const q = search.toLowerCase().trim();
    return permissionGroups
      .map(({ group, permissions: perms }) => {
        const groupLabel = getGroupLabel(group).toLowerCase();
        // If group name matches, show all its permissions
        if (groupLabel.includes(q)) {
          return { group, permissions: perms };
        }
        // Otherwise filter individual permissions
        const filtered = perms.filter((perm) => {
          const actionLabel = getActionLabel(perm.name).toLowerCase();
          return actionLabel.includes(q) || perm.name.toLowerCase().includes(q);
        });
        return { group, permissions: filtered };
      })
      .filter(({ permissions: perms }) => perms.length > 0);
  }, [permissionGroups, search, getActionLabel, getGroupLabel]);

  // Bucket the (already-filtered) groups under sidebar-driven categories.
  // Order follows NAV_GROUP_ORDER; categories with no visible groups are
  // dropped. Anything without a sidebar nav item lands in an "Other" bucket.
  type FilteredGroup = (typeof filteredGroups)[number];
  type CategorySection = {
    id: string;
    i18nKey: string;
    groups: FilteredGroup[];
  };
  const categorizedFilteredGroups = useMemo<CategorySection[]>(() => {
    const byCategory = new Map<string, FilteredGroup[]>();
    const uncategorized: FilteredGroup[] = [];

    for (const entry of filteredGroups) {
      const catId = getGroupCategory(entry.group);
      if (catId) {
        const arr = byCategory.get(catId) ?? [];
        arr.push(entry);
        byCategory.set(catId, arr);
      } else {
        uncategorized.push(entry);
      }
    }

    const ordered: CategorySection[] = [];
    for (const cat of PERMISSION_CATEGORIES) {
      const groups = byCategory.get(cat.id);
      if (groups && groups.length > 0) {
        // Order groups within the category to match nav-config.ts (the order
        // they appear in PERMISSION_CATEGORIES is derived from navItems).
        // Anything not in the canonical list falls to the end.
        const orderIndex = new Map(cat.groups.map((g, i) => [g, i]));
        groups.sort((a, b) => {
          const ai = orderIndex.get(a.group) ?? Number.MAX_SAFE_INTEGER;
          const bi = orderIndex.get(b.group) ?? Number.MAX_SAFE_INTEGER;
          return ai - bi;
        });
        ordered.push({ id: cat.id, i18nKey: cat.i18nKey, groups });
      }
    }
    if (uncategorized.length > 0) {
      ordered.push({
        id: '_uncategorized',
        i18nKey: '',
        groups: uncategorized
      });
    }
    return ordered;
  }, [filteredGroups]);

  // Children to render nested under each parent group. Lets the top-level
  // iteration skip child groups (they don't render as siblings of their
  // parent) and the parent's CollapsibleContent pull them in below its own
  // permissions list.
  const childGroupsByParent = useMemo<Record<string, FilteredGroup[]>>(() => {
    const map: Record<string, FilteredGroup[]> = {};
    for (const entry of filteredGroups) {
      const parent = CHILD_GROUP_MAP[entry.group];
      if (parent) {
        (map[parent] ??= []).push(entry);
      }
    }
    return map;
  }, [filteredGroups]);

  const savePermissions = useCallback(
    (permissions: string[]) => {
      if (!role) return;
      const validNames = new Set(allPermissions.map((p) => p.name));
      const cleaned = permissions.filter((n) => validNames.has(n));

      // A save is already on the wire — remember only the newest state and let
      // the in-flight request's onSettled send it. Overlapping PUTs are what
      // produced the duplicate-key error on the roles page.
      if (inFlightRef.current) {
        queuedPermissionsRef.current = cleaned;

        return;
      }

      const send = (payload: string[]) => {
        inFlightRef.current = true;
        updateMutation.mutate(
          {
            id: role.id,
            data: {
              name: role.name,
              name_en: role.name_en ?? '',
              name_ar: role.name_ar ?? '',
              color: role.color,
              permissions: payload
            }
          },
          {
            onSuccess: () => {
              if (savedIndicatorTimerRef.current) {
                clearTimeout(savedIndicatorTimerRef.current);
              }
              setShowSavedIndicator(true);
              savedIndicatorTimerRef.current = setTimeout(
                () => setShowSavedIndicator(false),
                1500
              );
            },
            onSettled: () => {
              inFlightRef.current = false;
              const queued = queuedPermissionsRef.current;
              queuedPermissionsRef.current = null;
              // Flush on failure too: the queued set is the operator's latest
              // intent, and dropping it would silently lose their last toggles.
              if (queued) send(queued);
            }
          }
        );
      };

      send(cleaned);
    },
    [role, updateMutation, allPermissions]
  );

  const scheduleSave = useCallback(
    (next: string[]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => savePermissions(next), 500);
    },
    [savePermissions]
  );

  const togglePermission = useCallback(
    (permName: string) => {
      setLocalPermissions((prev) => {
        const next = prev.includes(permName)
          ? prev.filter((p) => p !== permName)
          : [...prev, permName];
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const toggleGroupPermissions = useCallback(
    (permNames: string[]) => {
      setLocalPermissions((prev) => {
        const allActive = permNames.every((n) => prev.includes(n));
        const next = allActive
          ? prev.filter((p) => !permNames.includes(p))
          : [...new Set([...prev, ...permNames])];
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  /**
   * Presets act on the action permissions only — the view/column half has
   * its own bulk controls inside its own tab, and a preset that silently
   * wiped 336 column permissions would be indistinguishable from a bug.
   */
  const applyPreset = useCallback(
    (preset: 'full' | 'readOnly' | 'clear') => {
      const actionNames = displayPermissions.map((p: Permission) => p.name);
      const actionSet = new Set(actionNames);
      setLocalPermissions((prev) => {
        const kept = prev.filter((n) => !actionSet.has(n));
        const granted =
          preset === 'full'
            ? actionNames
            : preset === 'readOnly'
              ? actionNames.filter((n) => getPermissionAction(n) === 'view')
              : [];
        const next = [...new Set([...kept, ...granted])];
        scheduleSave(next);
        return next;
      });
    },
    [displayPermissions, scheduleSave]
  );

  /**
   * Write path for the embedded view/columns tab. It hands back the whole
   * permission array (action names included, untouched), so it rides the
   * same debounced autosave as every switch in the Actions tab — one role,
   * one permission set, one save cycle.
   */
  const replacePermissions = useCallback(
    (next: string[]) => {
      setLocalPermissions(next);
      scheduleSave(next);
    },
    [scheduleSave]
  );

  // Group/category collapse state. While searching, sections are always
  // expanded so matches are visible; otherwise the user's explicit choice
  // wins, defaulting to open.
  const isGroupOpen = useCallback(
    (group: string) => (hasSearch ? true : (groupOpenState[group] ?? true)),
    [hasSearch, groupOpenState]
  );
  const setGroupOpen = useCallback((group: string, open: boolean) => {
    setGroupOpenState((prev) => ({ ...prev, [group]: open }));
  }, []);
  const isCategoryOpen = useCallback(
    (id: string) => (hasSearch ? true : (categoryOpenState[id] ?? true)),
    [hasSearch, categoryOpenState]
  );
  const setCategoryOpen = useCallback((id: string, open: boolean) => {
    setCategoryOpenState((prev) => ({ ...prev, [id]: open }));
  }, []);

  const visibleGroupKeys = useMemo(
    () => permissionGroups.map((g) => g.group),
    [permissionGroups]
  );
  const allCollapsed =
    !hasSearch &&
    visibleGroupKeys.length > 0 &&
    visibleGroupKeys.every((g) => groupOpenState[g] === false);

  const toggleAllGroups = useCallback(() => {
    if (allCollapsed) {
      setGroupOpenState({});
      setCategoryOpenState({});
    } else {
      const nextGroups: Record<string, boolean> = {};
      for (const g of visibleGroupKeys) nextGroups[g] = false;
      setGroupOpenState(nextGroups);
    }
  }, [allCollapsed, visibleGroupKeys]);

  // Cleanup pending timers on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedIndicatorTimerRef.current) {
        clearTimeout(savedIndicatorTimerRef.current);
      }
    };
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose]
  );

  if (!activeRole) return null;

  const displayName = getRoleDisplayName(activeRole, locale);
  const activeCount = localPermissions.filter(
    (n) => !isViewColumnsPermission(n, hiddenGroups)
  ).length;
  // The view/column half is counted separately so the header never implies
  // that "118/118" also covers the hundreds of column permissions living in
  // the other tab. Mirrors RoleViewPermissionsPanel's own filter exactly.
  const viewScopeNames = new Set(
    allPermissions
      .filter(
        (p) =>
          (isColumnPermission(p.name) || p.name.endsWith('.view_filter')) &&
          !isHiddenPermission(p.name, hiddenGroups)
      )
      .map((p) => p.name)
  );
  const viewTotal = viewScopeNames.size;
  const viewActive = localPermissions.filter((n) =>
    viewScopeNames.has(n)
  ).length;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex w-full flex-col gap-0 p-0 sm:max-w-2xl lg:max-w-5xl'
        onOpenAutoFocus={(e) => {
          // Override Radix's default focus (the close button) and focus
          // the search input instead so users can type immediately.
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        {/* Header */}
        <SheetHeader className='space-y-2 border-b p-4 pe-12'>
          {/* The badge is the sheet's visible heading, so it carries heading
              weight rather than the chip-scale text used in table cells. */}
          <RoleColorBadge
            name={displayName}
            color={activeRole.color}
            className='px-3 py-1 text-sm font-semibold'
          />
          <SheetTitle className='sr-only'>{displayName}</SheetTitle>
          <SheetDescription asChild>
            <p className='text-muted-foreground text-xs'>
              {t('activePermissions', {
                count: activeCount,
                total: totalPermissions
              })}
              {' · '}
              {t('viewPermissionsCount', {
                count: viewActive,
                total: viewTotal
              })}
            </p>
          </SheetDescription>
        </SheetHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as typeof tab)}
          className='flex min-h-0 flex-1 flex-col gap-0'
        >
          <TabsList className='mx-3 mt-3 shrink-0 self-start'>
            <TabsTrigger value='actions' className='gap-1.5'>
              {t('tabActions')}
              <span className='text-muted-foreground text-[10px] tabular-nums'>
                {activeCount}/{totalPermissions}
              </span>
            </TabsTrigger>
            <TabsTrigger value='views' className='gap-1.5'>
              {t('tabViewsAndColumns')}
              <span className='text-muted-foreground text-[10px] tabular-nums'>
                {viewActive}/{viewTotal}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value='actions'
            className='flex min-h-0 flex-1 flex-col gap-0'
          >
            {/* Search + presets */}
            <div className='space-y-2.5 border-b p-3'>
              <div className='relative'>
                <Search className='text-muted-foreground pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2' />
                <Input
                  ref={searchInputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && hasSearch) {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearch('');
                    }
                  }}
                  placeholder={t('searchPermissions')}
                  className={cn('h-8 ps-8 text-sm', hasSearch && 'pe-8')}
                />
                {hasSearch && (
                  <button
                    type='button'
                    onClick={() => {
                      setSearch('');
                      searchInputRef.current?.focus();
                    }}
                    aria-label={t('clearSearch')}
                    className='text-muted-foreground hover:text-foreground absolute end-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm transition-colors'
                  >
                    <X className='h-3.5 w-3.5' />
                  </button>
                )}
              </div>

              {canEdit && (
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  {/* Three presets, not one pill per verb. Twelve of the
                      sixteen verbs this row used to list applied to exactly
                      one permission each, so they read as mystery categories
                      rather than bulk actions. Those verbs now appear only
                      inside the group that owns them. */}
                  <div className='flex flex-wrap gap-1.5'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='h-7 gap-1.5 text-xs'
                      onClick={() => applyPreset('full')}
                    >
                      <Sparkles className='h-3.5 w-3.5' />
                      {t('presetFullAccess')}
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='h-7 gap-1.5 text-xs'
                      onClick={() => applyPreset('readOnly')}
                    >
                      <Eye className='h-3.5 w-3.5' />
                      {t('presetReadOnly')}
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      className='text-muted-foreground hover:text-destructive h-7 gap-1.5 text-xs'
                      onClick={() => applyPreset('clear')}
                    >
                      <Eraser className='h-3.5 w-3.5' />
                      {t('presetClearAll')}
                    </Button>
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    disabled={hasSearch || visibleGroupKeys.length === 0}
                    className='h-7 gap-1.5 text-xs'
                    onClick={toggleAllGroups}
                  >
                    {allCollapsed ? (
                      <ChevronsUpDown className='h-3.5 w-3.5' />
                    ) : (
                      <ChevronsDownUp className='h-3.5 w-3.5' />
                    )}
                    {allCollapsed ? t('expandAll') : t('collapseAll')}
                  </Button>
                </div>
              )}
            </div>

            {/* Permissions list */}
            <ScrollArea className='min-h-0 flex-1'>
              <div className='space-y-3 p-2'>
                {filteredGroups.length === 0 ? (
                  <p className='text-muted-foreground px-3 py-4 text-center text-sm'>
                    {t('noPermissionsFound')}
                  </p>
                ) : (
                  categorizedFilteredGroups.map((category) => {
                    let categoryTotal = 0;
                    let categoryActive = 0;
                    for (const g of category.groups) {
                      for (const p of g.permissions) {
                        categoryTotal += 1;
                        if (localPermissions.includes(p.name))
                          categoryActive += 1;
                      }
                    }
                    const categoryAllActive =
                      categoryTotal > 0 && categoryActive === categoryTotal;

                    return (
                      <Collapsible
                        key={category.id}
                        open={isCategoryOpen(category.id)}
                        onOpenChange={(o) => setCategoryOpen(category.id, o)}
                        className='space-y-1'
                      >
                        <CollapsibleTrigger className='hover:bg-muted/60 group/cat flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-start'>
                          <span className='text-muted-foreground flex-1 text-[11px] font-semibold tracking-wide uppercase'>
                            {category.i18nKey
                              ? tRoot(category.i18nKey)
                              : t('group_other')}
                          </span>
                          <span
                            className={cn(
                              'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                              categoryAllActive
                                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                : 'bg-muted text-muted-foreground'
                            )}
                          >
                            {categoryActive}/{categoryTotal}
                          </span>
                          <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]/cat:rotate-180' />
                        </CollapsibleTrigger>
                        <CollapsibleContent className='space-y-1'>
                          {category.groups.map(
                            ({ group, permissions: perms }) => {
                              // Child groups (e.g. print_batches under page_indexes)
                              // are rendered nested inside their parent's content
                              // below — skip them at the top level so they don't
                              // appear as siblings.
                              if (CHILD_GROUP_MAP[group]) return null;
                              const children = childGroupsByParent[group] ?? [];
                              const permNames = perms.map((p) => p.name);
                              const activeInGroup = permNames.filter((n) =>
                                localPermissions.includes(n)
                              ).length;
                              const allActive = activeInGroup === perms.length;
                              const someActive =
                                activeInGroup > 0 && !allActive;
                              const groupKey =
                                GROUP_KEY_MAP[group] ?? 'group_other';

                              return (
                                <Collapsible
                                  key={group}
                                  open={isGroupOpen(group)}
                                  onOpenChange={(o) => setGroupOpen(group, o)}
                                >
                                  <div className='flex items-center gap-1'>
                                    {canEdit && (
                                      <Checkbox
                                        checked={
                                          allActive
                                            ? true
                                            : someActive
                                              ? 'indeterminate'
                                              : false
                                        }
                                        onCheckedChange={() =>
                                          toggleGroupPermissions(permNames)
                                        }
                                        className='ms-3'
                                      />
                                    )}
                                    <CollapsibleTrigger
                                      className={cn(
                                        'hover:bg-muted flex flex-1 items-center gap-2 rounded-md px-2 py-2 text-start',
                                        !canEdit && 'ms-3'
                                      )}
                                    >
                                      <div
                                        className={cn(
                                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                                          allActive
                                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                            : 'bg-muted text-muted-foreground'
                                        )}
                                      >
                                        {GROUP_ICON_MAP[group] ? (
                                          <Icon
                                            icon={GROUP_ICON_MAP[group]}
                                            className='h-3.5 w-3.5'
                                          />
                                        ) : (
                                          <span
                                            className={cn(
                                              'h-2 w-2 rounded-full',
                                              allActive
                                                ? 'bg-emerald-500'
                                                : 'bg-muted-foreground/30'
                                            )}
                                          />
                                        )}
                                      </div>
                                      <span className='flex-1 text-sm font-medium'>
                                        {t(groupKey)}
                                      </span>
                                      <span
                                        className={cn(
                                          'text-xs font-medium',
                                          allActive
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : activeInGroup === 0
                                              ? 'text-muted-foreground'
                                              : 'text-muted-foreground'
                                        )}
                                      >
                                        {allActive
                                          ? t('allOn')
                                          : activeInGroup === 0
                                            ? t('allOff')
                                            : `${activeInGroup}/${perms.length}`}
                                      </span>
                                      <ChevronDown className='text-muted-foreground h-4 w-4 shrink-0 transition-transform [[data-state=open]>&]:rotate-180' />
                                    </CollapsibleTrigger>
                                  </div>
                                  <CollapsibleContent>
                                    <div className='grid gap-x-4 gap-y-1 py-1 ps-7 sm:grid-cols-2 lg:grid-cols-3'>
                                      {perms.map((perm) => {
                                        const isActive =
                                          localPermissions.includes(perm.name);
                                        const actionLabel = getActionLabel(
                                          perm.name
                                        );
                                        return (
                                          <div
                                            key={perm.id}
                                            className='flex items-center gap-2 rounded-md px-3 py-1.5'
                                          >
                                            <span className='text-muted-foreground flex-1 text-sm'>
                                              {actionLabel}
                                            </span>
                                            <Switch
                                              checked={isActive}
                                              disabled={!canEdit}
                                              onCheckedChange={() =>
                                                togglePermission(perm.name)
                                              }
                                              className='shrink-0'
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {children.length > 0 && (
                                      <div className='border-muted ms-6 space-y-1 border-s-2 ps-2'>
                                        {children.map((child) => {
                                          const childPermNames =
                                            child.permissions.map(
                                              (p) => p.name
                                            );
                                          const childActive =
                                            childPermNames.filter((n) =>
                                              localPermissions.includes(n)
                                            ).length;
                                          const childAllActive =
                                            childActive ===
                                            child.permissions.length;
                                          const childSomeActive =
                                            childActive > 0 && !childAllActive;
                                          const childKey =
                                            GROUP_KEY_MAP[child.group] ??
                                            'group_other';
                                          return (
                                            <Collapsible
                                              key={child.group}
                                              open={isGroupOpen(child.group)}
                                              onOpenChange={(o) =>
                                                setGroupOpen(child.group, o)
                                              }
                                            >
                                              <div className='flex items-center gap-1'>
                                                {canEdit && (
                                                  <Checkbox
                                                    checked={
                                                      childAllActive
                                                        ? true
                                                        : childSomeActive
                                                          ? 'indeterminate'
                                                          : false
                                                    }
                                                    onCheckedChange={() =>
                                                      toggleGroupPermissions(
                                                        childPermNames
                                                      )
                                                    }
                                                    className='ms-2'
                                                  />
                                                )}
                                                <CollapsibleTrigger
                                                  className={cn(
                                                    'hover:bg-muted flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-start',
                                                    !canEdit && 'ms-2'
                                                  )}
                                                >
                                                  <span className='flex-1 text-xs font-medium'>
                                                    {t(childKey)}
                                                  </span>
                                                  <span
                                                    className={cn(
                                                      'text-[10px] font-medium',
                                                      childAllActive
                                                        ? 'text-emerald-600 dark:text-emerald-400'
                                                        : 'text-muted-foreground'
                                                    )}
                                                  >
                                                    {childAllActive
                                                      ? t('allOn')
                                                      : childActive === 0
                                                        ? t('allOff')
                                                        : `${childActive}/${child.permissions.length}`}
                                                  </span>
                                                  <ChevronDown className='text-muted-foreground h-3 w-3 shrink-0 transition-transform [[data-state=open]>&]:rotate-180' />
                                                </CollapsibleTrigger>
                                              </div>
                                              <CollapsibleContent>
                                                <div className='grid gap-x-4 gap-y-1 py-1 ps-6 sm:grid-cols-2 lg:grid-cols-3'>
                                                  {child.permissions.map(
                                                    (perm) => {
                                                      const isActive =
                                                        localPermissions.includes(
                                                          perm.name
                                                        );
                                                      return (
                                                        <div
                                                          key={perm.id}
                                                          className='flex items-center gap-2 rounded-md px-3 py-1'
                                                        >
                                                          <span className='text-muted-foreground flex-1 text-xs'>
                                                            {getActionLabel(
                                                              perm.name
                                                            )}
                                                          </span>
                                                          <Switch
                                                            checked={isActive}
                                                            disabled={!canEdit}
                                                            onCheckedChange={() =>
                                                              togglePermission(
                                                                perm.name
                                                              )
                                                            }
                                                            className='shrink-0'
                                                          />
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                                </div>
                                              </CollapsibleContent>
                                            </Collapsible>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </CollapsibleContent>
                                </Collapsible>
                              );
                            }
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent
            value='views'
            className='flex min-h-0 flex-1 flex-col overflow-hidden p-3'
          >
            <RoleViewPermissionsPanel
              permissions={localPermissions}
              onChange={replacePermissions}
              readOnly={!canEdit}
            />
          </TabsContent>
        </Tabs>

        {/* Saving / saved indicator */}
        {(updateMutation.isPending || showSavedIndicator) && (
          <div className='flex items-center justify-center gap-2 border-t p-2'>
            {updateMutation.isPending ? (
              <>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span className='text-muted-foreground text-xs'>
                  {t('saveChanges')}...
                </span>
              </>
            ) : (
              <>
                <Check className='h-3 w-3 text-emerald-600 dark:text-emerald-400' />
                <span className='text-xs font-medium text-emerald-600 dark:text-emerald-400'>
                  {t('saved')}
                </span>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
